/**
 * A AGENDA DE CULTOS de uma congregação no mês, só leitura (fase 6 do espelho, 19/10/2026).
 *
 * A marcação é a da tela "Agenda" do notebook (`Agenda.tsx` e `PadraoSemana.tsx`): o padrão da
 * semana, um dia por linha, com a frase de uma linha embaixo; e os cultos do mês, com DIA, DIA DA
 * SEMANA, CULTO e DESTINO DA OFERTA, o dia escrito só no primeiro culto de cada dia, e a etiqueta
 * "oferta lançada". Montar, copiar, mover e tirar cultos continua sendo no notebook.
 */
import type { JSX } from 'react'
import { servidor, type AgendaDoMes, type Eu } from '../api'
import { AtualizadoAs } from '../componentes/AtualizadoAs'
import { BarraDeCima } from '../componentes/BarraDeCima'
import { NavegarMes, useTelaDoMes } from '../componentes/TelaDoMes'
import { ehParaImprimir, enderecoDaCongregacao, enderecoDaTelaDaCongregacao, irPara } from '../rotas'

export function Agenda({
  eu, id, competenciaDoEndereco, aoSair
}: {
  eu: Eu
  id: string
  competenciaDoEndereco: string | null
  aoSair: () => void
}): JSX.Element {
  const paraImprimir = ehParaImprimir()
  const { dados, erro, atualizacao } = useTelaDoMes<AgendaDoMes>(
    `${id}|${competenciaDoEndereco ?? ''}`,
    () => servidor.agenda(id, competenciaDoEndereco),
    aoSair,
    paraImprimir
  )
  const mes = dados?.competencia ?? competenciaDoEndereco ?? null
  const andar = (para: string): void => irPara(enderecoDaTelaDaCongregacao('agenda', id, para))

  return (
    <>
      {!paraImprimir && (
        <BarraDeCima eu={eu} aoSair={aoSair} voltarPara={{ endereco: enderecoDaCongregacao(id, mes), texto: 'Folhas' }} />
      )}
      <main className="folha folha-lancamento tela-da-agenda" data-pronto={dados !== null ? 'sim' : erro !== null ? 'erro' : 'nao'}>
        {erro !== null && <p className="recado" role="alert">{erro}</p>}
        {dados === null && erro === null && <p className="recado aguarde">Carregando...</p>}

        {dados !== null && (
          <>
            {!paraImprimir && <AtualizadoAs atualizacao={atualizacao} />}
            {mes !== null && !paraImprimir && <NavegarMes mes={mes} andar={andar} />}

            <h1>AGENDA DE CULTOS – {dados.competencia_por_extenso.toUpperCase()}</h1>
            <p className="explicacao centro">
              {dados.congregacao.nome} · {dados.cultos.length === 1 ? '1 culto' : `${dados.cultos.length} cultos`}
              {dados.cultos.length > 0 && <>, {dados.com_oferta === 1 ? '1 com oferta lançada' : `${dados.com_oferta} com oferta lançada`}</>}
            </p>

            <table className="grade agenda-do-mes">
              <colgroup>
                <col style={{ width: '9%' }} /><col style={{ width: '23%' }} />
                <col style={{ width: '28%' }} /><col style={{ width: '40%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th className="rotulo">DIA</th><th className="rotulo">DIA DA SEMANA</th>
                  <th className="rotulo">CULTO</th><th className="rotulo">DESTINO DA OFERTA</th>
                </tr>
              </thead>
              <tbody>
                {dados.cultos.map((c) => (
                  <tr key={c.id} className={c.dia !== '' ? 'primeiro-do-dia' : undefined}>
                    <td className="impresso">{c.dia}</td>
                    <td className="impresso">{c.dia_semana}</td>
                    <td className="impresso">
                      {c.culto ?? <span className="lapis">culto comum</span>}
                      {c.oferta_lancada && <span className="etiqueta">oferta lançada</span>}
                    </td>
                    <td>{c.destino_da_oferta ?? <span className="lapis">fica na congregação</span>}</td>
                  </tr>
                ))}
                {dados.cultos.length === 0 && (
                  <tr><td colSpan={4} className="lapis">Nenhum culto programado para {dados.competencia_por_extenso}.</td></tr>
                )}
              </tbody>
            </table>

            <h2>Padrão da semana{dados.padrao_da_semana.inicial ? ' (inicial)' : ''}</h2>
            <table className="grade padrao-da-semana">
              <colgroup><col style={{ width: '36%' }} /><col /></colgroup>
              <tbody>
                {dados.padrao_da_semana.dias.map((d) => (
                  <tr key={d.dia_semana}>
                    <th className="rotulo">{d.dia_semana}</th>
                    <td>
                      {d.cultos.length === 0
                        ? <span className="lapis">sem culto</span>
                        : d.cultos.map((nome, i) => <span key={`${nome}-${i}`} className="etiqueta">{nome}</span>)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="explicacao padrao-da-semana">
              Padrão da semana{dados.padrao_da_semana.inicial ? ' (inicial)' : ''}: {dados.padrao_da_semana.resumo}.
              {' '}{dados.padrao_da_semana.por_semana === 1 ? '1 culto por semana' : `${dados.padrao_da_semana.por_semana} cultos por semana`}.
            </p>
            {dados.padrao_da_semana.inicial && (
              <p className="explicacao">
                Ninguém guardou o padrão desta congregação no notebook ainda; este é o inicial, que o
                notebook usa para montar o mês.
              </p>
            )}
          </>
        )}
      </main>
    </>
  )
}
